import React, { useEffect, useMemo, useState } from 'react';
import { Outlet, useSearchParams } from 'react-router-dom';
import styled from 'styled-components';
import { useQuery } from 'react-query';
import axios from 'axios';

import { getUserNoFromToken } from '../utils/auth';

const LayoutContainer = styled.div`
  /* [변경 사유]
   * - 상담 대시보드는 공통 Layout(헤더/사이드바) 하위 컨텐츠 영역에서 렌더링되어야 합니다.
   * - 따라서 전용 레이아웃처럼 화면 전체 높이(100vh)와 스크롤을 강제하지 않고,
   *   일반 페이지 컨테이너처럼 동작하도록 조정합니다.
   */
  display: flex;
  flex-direction: column;
  width: 100%;
`;

const SimpleHeader = styled.header`
  /* [변경 사유]
   * - 기존 전용 헤더는 공통 헤더(AI 닥터큐)와 중복되어 UX가 어색합니다.
   * - 대신 컨텐츠 영역 상단에 "컨트롤 바" 형태로 환자 선택만 제공하도록 톤다운합니다.
   * - 카드 섹션(Trend/Timeline 등)과 좌측 시작선이 어긋나 제목이 잘려 보일 수 있어,
   *   DashboardContainer의 폭/패딩 기준(최대 1024px, padding 0 16px, 가운데 정렬)에 맞춰 정렬합니다.
   */
  height: 44px;
  background-color: #ffffff;
  border: 1px solid #edf2f7;
  border-radius: 12px;
  display: flex;
  align-items: center;
  /* [변경 사유]
   * - ConsultationDashboardPage의 DashboardContainer와 동일한 컨테이너 규격으로 맞춰
   *   상단 타이틀바/하단 카드들의 시작점을 동일선상으로 정렬합니다.
   */
  max-width: 1024px;
  width: 100%;
  margin: 0 auto;
  padding: 0 16px;
  box-sizing: border-box;
  justify-content: space-between;
  margin-bottom: 16px;
`;

const LogoArea = styled.div`
  font-size: 1.2rem;
  font-weight: 700;
  color: #333;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const ConsultantInfo = styled.div`
  font-size: 0.9rem;
  color: #666;
`;

const PatientDropdownWrapper = styled.div`
  position: relative;
  max-width: 220px;
`;

const PatientDropdownTrigger = styled.button`
  font-size: 0.9rem;
  color: #2d3748;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid #e2e8f0;
  background: #ffffff;
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  cursor: pointer;
  white-space: nowrap;
`;

const PatientDropdownList = styled.div`
  position: absolute;
  top: calc(100% + 4px);
  right: 0;
  min-width: 160px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  box-shadow: 0 4px 12px rgba(0,0,0,0.12);
  z-index: 200;
  overflow: hidden;
`;

const PatientDropdownItem = styled.div`
  padding: 10px 14px;
  font-size: 0.9rem;
  color: ${props => props.selected ? '#2563eb' : '#2d3748'};
  background: ${props => props.selected ? '#eff6ff' : '#ffffff'};
  cursor: pointer;
  &:hover {
    background: #f1f5f9;
  }
`;

const ContentArea = styled.main`
  flex: 1;
  /* [변경 사유]
   * - main-content(공통 Layout)에서 이미 여백/스크롤을 관리합니다.
   * - 상담 화면 내부에서 중복 스크롤/패딩이 생기지 않도록 최소화합니다.
   */
  padding: 0;
  position: relative;
  
  /* Customized Scrollbar */
  &::-webkit-scrollbar {
    width: 6px;
    height: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    background: #ccc;
    border-radius: 3px;
  }
`;

const ConsultationLayout = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedPid = searchParams.get('std_patient_id') || '';
  const [patientDropdownOpen, setPatientDropdownOpen] = useState(false);

  // [변경 사유]
  // - 상담 대시보드 URL에서 user_no 노출을 제거하기 위해, userNo는 라우트 파라미터로 받지 않습니다.
  // - 토큰 payload(sub) 또는 localStorage(user_no) 기반으로 로그인 사용자 userNo를 내부에서 획득합니다.
  const userNo = getUserNoFromToken() || window.localStorage.getItem('user_no') || '';

  const apiBase = (process.env.REACT_APP_API_URL || 'http://localhost:8000').replace(/\/+$/, '');

  const { data: patientsPayload } = useQuery(
    ['consultationPatients', userNo],
    async () => {
      if (!userNo) return { patients: [] };
      const res = await axios.get(`${apiBase}/api/v1/consultation/${userNo}/patients?t=${new Date().getTime()}`);
      return res.data;
    },
    {
      enabled: !!userNo,
      staleTime: 30_000,
      retry: 1,
    }
  );

  const patients = useMemo(
    () => (Array.isArray(patientsPayload?.patients) ? patientsPayload.patients : []),
    [patientsPayload?.patients],
  );

  useEffect(() => {
    if (!patientDropdownOpen) return;
    const handler = () => setPatientDropdownOpen(false);
    document.addEventListener('mousedown', handler);
    document.addEventListener('touchstart', handler);
    return () => {
      document.removeEventListener('mousedown', handler);
      document.removeEventListener('touchstart', handler);
    };
  }, [patientDropdownOpen]);

  // [변경 사유]
  // - 최초 진입 시에는 기존 로직(최신 1명)을 그대로 따르되,
  //   드롭다운을 안정적으로 표시하기 위해 기본 std_patient_id를 querystring에 주입합니다.
  useEffect(() => {
    if (!userNo) return;
    if (selectedPid) return;
    if (!patients || patients.length === 0) return;
    const defaultItem = patients.find((p) => p?.is_default) || patients[0];
    const pid = defaultItem?.std_patient_id;
    if (!pid) return;
    setSearchParams({ std_patient_id: String(pid) }, { replace: true });
  }, [userNo, patients, selectedPid, setSearchParams]);

  return (
    <LayoutContainer>
      <SimpleHeader>
        <LogoArea>
          <span>상담 대시보드</span>
        </LogoArea>
        <ConsultantInfo>
          <PatientDropdownWrapper>
            <PatientDropdownTrigger
              type="button"
              onClick={() => setPatientDropdownOpen(o => !o)}
              disabled={patients.length === 0}
            >
              <span>
                {patients.length === 0
                  ? '분석 대상자 없음'
                  : (patients.find(p => String(p?.std_patient_id) === selectedPid)?.label || '환자 선택')}
              </span>
              <span>{patientDropdownOpen ? '▲' : '▼'}</span>
            </PatientDropdownTrigger>
            {patientDropdownOpen && patients.length > 0 && (
              <PatientDropdownList
                onMouseDown={e => e.stopPropagation()}
                onTouchStart={e => e.stopPropagation()}
              >
                {patients.map((p) => {
                  const pid = String(p?.std_patient_id);
                  return (
                    <PatientDropdownItem
                      key={pid}
                      selected={pid === selectedPid}
                      onClick={() => {
                        setSearchParams({ std_patient_id: pid }, { replace: true });
                        setPatientDropdownOpen(false);
                      }}
                    >
                      {p?.label || `환자 ${pid}`}
                    </PatientDropdownItem>
                  );
                })}
              </PatientDropdownList>
            )}
          </PatientDropdownWrapper>
        </ConsultantInfo>
      </SimpleHeader>
      <ContentArea>
        <Outlet />
      </ContentArea>
    </LayoutContainer>
  );
};

export default ConsultationLayout;
